// Model
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {
	ROLE,
	PATH,
	BASE_STATE,
	SIGNAL_EXCEPTIONS_EVENTS,
	AllowedSignalRModule,
	CONFIG_API,
	CONFIG_ROUTE
} from '../model/enums.model';
import {AppServiceModel, ObjectUtils, UserContextModel} from '@cv-invision/core';
import { UserContext } from '../../models/user-context';

@Injectable()
export class QcAppService extends AppServiceModel {

	public httpInterceptorExceptions = {
		toaster: [
			/\/rule-sets\/resolve-by-criteria\/?/gm,
			/(\/request-(.+)-document\/(.+)\/status\/?)/gm,
			/\/request-[\W\w]+-document\/[\W\w]+\/status\/?/gm,
			/\/desktop-intelligence\/rules\/?/gm,
			/\/orders-api\/utils\/integrations\/?/gm,
		],
		handledByOtherInterceptor: [
			// /\/rules-api\/rule-engine\/rule-sets\/available\/save/gm,
			// /\/qc-intelligence\/cache\/all/gm
			/\/desktop-intelligence\/?/gm,
			/\/orders-api\/products\/?/gm,
			/\/orders-api\/utils\/?/gm,
			/\/xpanse-api\/transactions\/?/gm,
			/\/nexsys-api\/transactions\/?/gm,
			/\/valutrust-api\/transactions\/?/gm,
			/\/identity\/classvaluation\/users\/validate-credentials\/?/gm,
			/\/orders-external-api\/transactions\/class\/?/gm,
			/\/fnc-api\/transactions\/?/gm,
			/\/evo-api\/transactions\/?/gm,
		],
		// Exceptions that even when throwing a 404/400/500 are not necessary errors that prevent the page to load
		swallowValidErrors: [
			// For Old orders before Rules implementation the endpoint throws a 404
			/\/rule-sets\/resolve-by-criteria\/?/gm,
			/\/rules-api\/rule-management\/?/gm,
			/\/desktop-intelligence\/rules\/?/gm,
			/\/desktop-intelligence\/orders\/(.+)\/document\/lock?/gm,
			/\/xpanse-api\/transactions\/?/gm,
			/\/nexsys-api\/transactions\/?/gm,
			/\/valutrust-api\/transactions\/?/gm,
			/\/identity\/classvaluation\/users\/validate-credentials\/?/gm,
			/\/orders-external-api\/transactions\/class\/?/gm,
			/\/fnc-api\/transactions\/?/gm,
			/\/evo-api\/transactions\/?/gm,
			/\/(.+)\/ui-config\/?/gm,
		]
	};

	public signalRConnectionIdApps = [
		PATH.DESKTOP
	];

	public headerIgnoreFormTypeApps = [
		PATH.DESKTOP
	];

	constructor(private http: HttpClient) {
		super();
	}

	/**
	 * Given the user permissions, generate the App header & module
	 *
	 * @param permissions
	 * @returns
	 */
	getAppHeader(permissions: any): any {
		const data = {
			logo: '',
			module: ''
		};

		if (permissions.includes(ROLE.INVISION_DESKTOP)) {
			data.logo = 'assets/img/brand/invision-desktop.svg';
			data.module = PATH.DESKTOP;
		} else if (permissions.includes(ROLE.INTELLIGENCE)) {
			data.logo = 'assets/img/brand/class-intelligence.svg';
			data.module = PATH.INTELLIGENCE;
		} else if (permissions.includes(ROLE.SYSTEM_STAFF)) {
			data.logo = 'assets/img/brand/user-management.svg';
			data.module = PATH.IDENTITY_SYSTEM;
		} else if (permissions.includes(ROLE.USERS_RULES)) {
			data.logo = 'assets/img/brand/rule-engine.svg';
			data.module = PATH.RULE_ENGINE;
		} else if (permissions.includes(ROLE.USER_STAFF_ADMIN)) {
			data.logo = 'assets/img/brand/global-configuration.svg';
			data.module = PATH.GLOBAL_CONFIGURATION;
		}

		return data;
	}

	/**
	 * Given the user permissions, generate the AppSwitcher and return the selected App based on the current Module
	 * @param user
	 * @param currentModule
	 */
	public getAppSwitcher(user: any, currentModule: string): any {
		const menuOptions = [];

		if (user.permission.includes(ROLE.INTELLIGENCE)) {
			menuOptions.push({
				type: 'option',
				label: PATH.INTELLIGENCE,
				image: 'assets/img/brand/class-intelligence.svg'
			});
			menuOptions.push({ type: 'separator' });
		}
		if (user.permission.includes(ROLE.INVISION_DESKTOP)) {
			menuOptions.push({
				type: 'option',
				label: PATH.DESKTOP,
				image: 'assets/img/brand/invision-desktop.svg'
			});
			menuOptions.push({ type: 'separator' });
		}
		if (user.permission.includes(ROLE.SYSTEM_STAFF)) {
			menuOptions.push({
				type: 'option',
				label: PATH.IDENTITY_SYSTEM,
				image: 'assets/img/brand/user-management.svg'
			});
			menuOptions.push({ type: 'separator' });
		}
		if (user.permission.includes(ROLE.USERS_RULES)) {
			menuOptions.push({
				type: 'option',
				label: PATH.RULE_ENGINE,
				image: 'assets/img/brand/rule-engine.svg'
			});
			menuOptions.push({ type: 'separator' });
		}
		if (user.permission.includes(ROLE.USER_STAFF_ADMIN)) {
			menuOptions.push({
				type: 'option',
				label: PATH.GLOBAL_CONFIGURATION,
				image: 'assets/img/brand/global-configuration.svg'
			});
		}

		if (user.permission.includes(ROLE.SMART_ASSIGN_CAN_ACCESS)) {
			menuOptions.push({ type: 'separator' });
			menuOptions.push({
				type: 'option',
				label: PATH.SMART_ASSIGN,
				image: 'assets/img/brand/smart-assign.svg'
			});
		}

		// remove the last element of the array only if it's a separator that makes no sense at the human eye.
		if (menuOptions[menuOptions.length - 1].type === 'separator') {
			menuOptions.pop();
		}

		// Select the Option based on the Current Module
		const optionIndex = menuOptions.findIndex((option: any) => option.label && currentModule.includes(option.label));
		const appDropdown = (menuOptions[optionIndex]) ? menuOptions[optionIndex] : {label: '', image: ''};

		return {
			options: menuOptions,
			appDropdown
		};
	}

	/**
	 * Given the module path, check if it can show the return to inbox.
	 *
	 * @param module
	 * @returns
	 */
	 getHeaderShowInboxButton(module: string): boolean {
		return (
			!module.includes(PATH.IDENTITY_SYSTEM) &&
			!module.includes(PATH.RULE_ENGINE) &&
			!module.includes(PATH.GLOBAL_CONFIGURATION)
		);
	}

	/**
	 * Given the user permissions, check if it can show the appSwitcher
	 *
	 * @param permissions
	 * @returns
	 */
	getCanAccessMultipleApps(permissions: any): boolean {
		let counter = permissions.includes(ROLE.INTELLIGENCE) ? 1 : 0;
		counter += permissions.includes(ROLE.INVISION_DESKTOP) ? 1 : 0;
		counter += permissions.includes(ROLE.SYSTEM_STAFF) ? 1 : 0;
		counter += permissions.includes(ROLE.USERS_RULES) ? 1: 0;

		return counter > 1;
	}

	/**
	 * Get the ROLE given the permissions array
	 *
	 * @param permissions
	 */
	public getRoleByPermission(permissions: any): any {
		const result = {
			return: false,
			role: ''
		};

		if (ROLE.ALL.every(r => permissions.includes(r))) {
			result.role = ROLE.ADMIN;
			result.return = true;

			return result;
		}

		if (permissions.includes(ROLE.INVISION_DESKTOP)) {
			result.role = ROLE.INVISION_DESKTOP;
			result.return = true;

			return result;
		}

		if (permissions.includes(ROLE.INTELLIGENCE)) {
			result.role = ROLE.INTELLIGENCE;
			result.return = true;

			return result;
		}

		if (permissions.includes(ROLE.SYSTEM_STAFF)) {
			result.role = ROLE.SYSTEM_STAFF;
			result.return = true;

			return result;
		}

		if (permissions.includes(ROLE.USERS_RULES)) {
			result.role = ROLE.USERS_RULES;
			result.return = true;

			return result;
		}

		if (permissions.includes(ROLE.USER_STAFF_ADMIN)) {
			result.role = ROLE.USER_STAFF_ADMIN;
			result.return = true;

			return result;
		}

		return result;
	}

	/**
	 * Get the ROLE given the permissions array & current url
	 *
	 * @param permissions
	 * @param url
	 */
	public getRoleByPermissionAndUrl(permissions: any, url: string): any {
		const result = {
			return: false,
			role: '',
			path: ''
		};

		// INTELLIGENCE
		if (permissions.includes(ROLE.INTELLIGENCE) && url.includes(PATH.INTELLIGENCE)) {
			result.path = PATH.INTELLIGENCE;
			result.role = ROLE.INTELLIGENCE;
		}

		//  APPRAISAL
		if (permissions.includes(ROLE.INVISION_DESKTOP) && url.includes(PATH.DESKTOP)) {
			result.path = PATH.APPRAISAL;
			result.role = ROLE.INVISION_DESKTOP;
		}

		// IDENTITY SYSTEM
		if (
			(permissions.includes(ROLE.SYSTEM_STAFF) && url.includes(PATH.IDENTITY_SYSTEM))
		) {
			result.path = PATH.IDENTITY_SYSTEM;
			result.role = ROLE.SYSTEM_STAFF;
		}

		// RULE EDITOR
		if (permissions.includes(ROLE.USERS_RULES) && url.includes(PATH.RULE_ENGINE)) {
			result.path = PATH.RULE_ENGINE;
			result.role = ROLE.USERS_RULES;
		}

		// GLOBAL CONFIGURATION
		if (permissions.includes(ROLE.USER_STAFF_ADMIN) && url.includes(PATH.GLOBAL_CONFIGURATION)) {
			result.path = PATH.GLOBAL_CONFIGURATION;
			result.role = ROLE.USER_STAFF_ADMIN;
		}

		// SMART ASSIGN
		if (permissions.includes(ROLE.SMART_ASSIGN_CAN_ACCESS) && url.includes(PATH.SMART_ASSIGN)) {
			result.path = PATH.SMART_ASSIGN;
			result.role = ROLE.SMART_ASSIGN_CAN_ACCESS;
		}

		// DESKTOP
		if (
			permissions.includes(ROLE.INVISION_DESKTOP) &&
			!permissions.includes(ROLE.INTELLIGENCE) &&
			url.includes(PATH.INTELLIGENCE)
		) {
			result.role = ROLE.INVISION_DESKTOP;
			result.return = true;

			return result;
		}
		// INTELLIGENCE
		if (
			!permissions.includes(ROLE.INVISION_DESKTOP) &&
			permissions.includes(ROLE.INTELLIGENCE) &&
			url.includes(PATH.DESKTOP)
		) {
			result.role = ROLE.INTELLIGENCE;
			result.return = true;

			return result;
		}

		// IDENTITY SYSTEM
		if (
			!permissions.includes(ROLE.SYSTEM_STAFF) &&
			permissions.includes(ROLE.INTELLIGENCE) &&
			(url.includes(PATH.IDENTITY_SYSTEM))
		) {
			result.role = ROLE.INTELLIGENCE;
			result.return = true;

			return result;
		}

		if (
			!permissions.includes(ROLE.SYSTEM_STAFF) &&
			permissions.includes(ROLE.INVISION_DESKTOP) &&
			(url.includes(PATH.IDENTITY_SYSTEM))
		) {
			result.role = ROLE.INVISION_DESKTOP;
			result.return = true;

			return result;
		}
		// RULE EDITOR
		if (
			!permissions.includes(ROLE.USERS_RULES) &&
			permissions.includes(ROLE.INTELLIGENCE) &&
			(url.includes(PATH.RULE_ENGINE))
		) {
			result.role = ROLE.INTELLIGENCE;
			result.return = true;

			return result;
		}

		if (
			!permissions.includes(ROLE.USERS_RULES) &&
			permissions.includes(ROLE.INVISION_DESKTOP) &&
			(url.includes(PATH.RULE_ENGINE))
		) {
			result.role = ROLE.INVISION_DESKTOP;
			result.return = true;

			return result;
		}

		// GLOBAL CONFIGURATION
		if (
			!permissions.includes(ROLE.USER_STAFF_ADMIN) &&
			permissions.includes(ROLE.INVISION_DESKTOP) &&
			url.includes(PATH.GLOBAL_CONFIGURATION)
		) {
			result.role = ROLE.INVISION_DESKTOP;
			result.return = true;

			return result;
		}

		if (
			!permissions.includes(ROLE.USER_STAFF_ADMIN) &&
			permissions.includes(ROLE.INTELLIGENCE) &&
			url.includes(PATH.GLOBAL_CONFIGURATION)
		) {
			result.role = ROLE.INTELLIGENCE;
			result.return = true;

			return result;
		}

		if (
			!permissions.includes(ROLE.USER_STAFF_ADMIN) &&
			permissions.includes(ROLE.SYSTEM_STAFF) &&
			url.includes(PATH.GLOBAL_CONFIGURATION)
		) {
			result.role = ROLE.SYSTEM_STAFF;
			result.return = true;

			return result;
		}

		return result;
	}

	/**
	 * Get the defautl PATH given the permissions
	 *
	 * @param permissions
	 */
	public getDefaultPathByPermissions(permissions: any): string {
		let path = '';

		if (permissions.includes(ROLE.INVISION_DESKTOP)) {
			path = PATH.DESKTOP;
		} else if (permissions.includes(ROLE.INTELLIGENCE)) {
			path = PATH.INTELLIGENCE;
		} else if (permissions.includes(ROLE.SYSTEM_STAFF)) {
			path = PATH.IDENTITY_SYSTEM;
		} else if (permissions.includes(ROLE.USERS_RULES)) {
			path = PATH.RULE_ENGINE;
		} else if (permissions.includes(ROLE.USER_STAFF_ADMIN)) {
			path = PATH.GLOBAL_CONFIGURATION;
		}

		return path;
	}

	/**
	 * Get the Default PATH given the url
	 * @param url
	 * @param currentModule
	 */
	public getDefaultPath(url: string, currentModule: string): any {
		const appModule: any =  {
			path: PATH.INTELLIGENCE,
			redirect: true
		};

		if ([PATH.DESKTOP, 'PFR', currentModule].some(excerpt => url.includes(excerpt))) {
			appModule.path = PATH.DESKTOP;
		}
		if (url.includes(PATH.INTELLIGENCE) && currentModule.includes(PATH.INTELLIGENCE)) {
			appModule.path = PATH.INTELLIGENCE;
		}
		if (url.includes(PATH.IDENTITY_SYSTEM) && currentModule.includes(PATH.IDENTITY_SYSTEM)) {
			appModule.path = PATH.IDENTITY_SYSTEM;
		}
		if (url.includes(PATH.RULE_ENGINE) && currentModule.includes(PATH.RULE_ENGINE)) {
			appModule.path = PATH.RULE_ENGINE;
		}
		if (url.includes(PATH.GLOBAL_CONFIGURATION) && currentModule.includes(PATH.GLOBAL_CONFIGURATION)) {
			appModule.path = PATH.GLOBAL_CONFIGURATION;
		}
		// Review if the URL is a valid Error by the Structure of the API
		appModule.redirect = !ObjectUtils.containsPattern(url, this.httpInterceptorExceptions.swallowValidErrors);

		return appModule;
	}

	/**
	 * Get the user data and menu from an endpoint or JWT
	 *
	 * @param userInfo The payload from the JWT decode
	 * @param token The JWT token
	 */
	getUserData(userInfo: any, token: string): Observable<UserContextModel> {

		const payload = {
			schema: UserContext.schema,
			navItems: UserContext.navItems,
		}

		const user = new UserContextModel();

		if (payload) {
			const {
				sub,
				first_name,
				last_name,
				email,
				permission,
				role,
				'cv.connection': cvConnection,
				name,
				orgName,
				orgType,
				orgId,
				userType,
			} = userInfo;
			user.id = sub;
			user.cvConnection = cvConnection;
			user.firstName = first_name;
			user.lastName = last_name;
			user.name = `${first_name} ${last_name}`;
			user.email = email;
			user.userName = name;
			user.userType = userType;
			user.organizationName = orgName;
			user.organizationId = orgId;
			user.organizationType = orgType;
			user.isActive = true;
			user.token = token;
			user.permission = (permission !== ROLE.NOTALLOWED) ? permission : [];
			user.role = (role !== ROLE.NOTALLOWED) ? role : [];
			user.navItems = payload.navItems;
			user.schema = payload.schema;
		}

		return of(user);
	}

	/**
	 * Update the user data using the token
	 *
	 * @param userInfo The payload from the JWT decode
	 * @param token The JWT token
	 */
	getUserDataFromToken(userInfo: any, token: string): Observable<UserContextModel> {
		const user = new UserContextModel();
		const {
			sub,
			first_name,
			last_name,
			email,
			permission,
			role,
			'cv.connection': cvConnection,
			name,
			orgName,
			orgType,
			orgId,
			userType,
		} = userInfo;

		user.id = sub;
		user.cvConnection = cvConnection;
		user.firstName = first_name;
		user.lastName = last_name;
		user.name = `${first_name} ${last_name}`;
		user.email = email;
		user.userName = name;
		user.userType = userType;
		user.organizationName = orgName;
		user.organizationId = orgId;
		user.organizationType = orgType;
		user.permission = permission;
		user.role = role;
		user.token = token;

		return of(user);
	}

	/**
	 * Get redirectionData given the url and role
	 *
	 * @param url
	 * @param role
	 * @returns
	 */
	getRedirectionData(url: string, role: any): any {
		const result = {
			path: '/',
			isAdmin: role === ROLE.ADMIN
		};

		switch (role) {
			case ROLE.INVISION_DESKTOP:
				result.path = url.includes(PATH.DESKTOP) ? url: PATH.DESKTOP;
				break;
			case ROLE.INTELLIGENCE:
				result.path = url.includes(PATH.INTELLIGENCE) ? url : PATH.INTELLIGENCE;
				break;
			case ROLE.SYSTEM_STAFF:
			    result.path = url.includes(PATH.IDENTITY_SYSTEM) ? url : PATH.IDENTITY_SYSTEM;
				break;
			case ROLE.USERS_RULES:
				result.path = url.includes(PATH.RULE_ENGINE) ? url : PATH.RULE_ENGINE;
				break;
			case ROLE.USER_STAFF_ADMIN:
				result.path = url.includes(PATH.GLOBAL_CONFIGURATION) ? url :PATH.GLOBAL_CONFIGURATION;
			    break;
		}

		if (result.isAdmin) {
			if(url === '/') {
				// default module for admins
				result.path = PATH.INTELLIGENCE;
			}else{
				result.path = url;
			}
		}

		return result;
	}

	/**
	 * Skip CanActivate from auth.guard
	 *
	 * @param currentRoute
	 * @param role
	 * @returns TRUE|FALSE
	 */
	getSkipCanActivateGuard(currentRoute: any, role: any): boolean {
		let skip = false;

		if (currentRoute && currentRoute.path === PATH.SYSTEM_INFO && role.includes(ROLE.ADMIN)) {
			skip = true;
		}

		return skip;
	}

	/**
	 * Get the module main url to use as base link for the notifications
	 *
	 * @param modulePath
	 * @returns
	 */
	getMainModuleUrl(modulePath: string): string {
		let mainUrl = null;

		switch (modulePath) {
			case PATH.INTELLIGENCE: mainUrl = '/intelligence/quality/overview'; break;
			case PATH.DESKTOP: mainUrl = '/desktop/order/initial'; break;
		}

		return mainUrl;
	}

	/**
	 * Get the appName to use for the telemetry connection
	 *
	 * @param url
	 */
	getTelemetryAppName(url: string): string {
		const appName = url.includes('desktop') ? 'Desktop' : 'Intelligence';

		return appName;
	}

	/**
	 * Verify if its needed a route change on error
	 *
	 * @param module
	 * @param errorMessage
	 * @returns
	 */
	getChangeRouteOnError(module: string, errorMessage: string): any {
		const result = {
			changeRoute: false,
			path: '',
			routerOptions: {}
		};

		if (module === PATH.RULE_ENGINE && errorMessage === 'Rule set not found') {
			result.changeRoute = true;
			result.path = '/rule-engine/rule-sets';
			result.routerOptions = {
				reload: true
			};
		}

		return result;
	}

	/**
	 * Get the signalR connection url given the user role
	 *
	 * @param module
	 * @param user
	 * @param config
	 * @returns
	 */
	getSignalRUrl(module: string, currentUrl: string, user: any, config: any): string {
		let url;
		const { signalRUrlInt, signalRUrlDesk } = config.api;
		const permission = user.permission.filter((r: any) => r !== 'empty');

		// check if the currrent module is allowed to connect to SignalR
		if(module && !this.allowedSignalRModule(AllowedSignalRModule, module)) {
			return url;
		}

		if (!module && currentUrl) {
			if (currentUrl.includes(PATH.INTELLIGENCE)) {
				url = signalRUrlInt;
			}
			if (currentUrl.includes(PATH.DESKTOP)) {
				url = signalRUrlDesk;
			}
		}else if(module){
			if (module.includes(PATH.INTELLIGENCE)) {
				url = signalRUrlInt;
			}
			if (module.includes(PATH.DESKTOP)) {
				url = signalRUrlDesk;
			}
		}else {
			if(permission.includes(ROLE.INTELLIGENCE)) {
				url = signalRUrlInt;
			}
			if(!permission.includes(ROLE.INTELLIGENCE) && permission.includes(ROLE.INVISION_DESKTOP)) {
				url = signalRUrlDesk;
			}
		}

		return url;
	}

	/**
	 * Returns false or true whether a list of module are valid for sinalR connection
	 * @param modules Array of allowed signalRmodules
	 * @param currentModule:string
	 * @return boolean
	 */
	allowedSignalRModule(modules:Array<string>, currentModule:string): boolean {
		return modules.includes(currentModule);
	}

	/**
     * Function to check if the signalR event can be stored in the state.
     * OPTIONAL IMPLEMENTATION:
     * If the app requires special logic (invisionUX), you have to implement it in the perApp service.
     * If not, the default value returned it is true;
     *
     * @param data An Object with the function's data required to process
     * @returns boolean
     */
	canSignalRStoreEvents(data: any): boolean {
		let canStore = false;
		if (data.user
			&& data.data.user
			&& data.user.id !== data.data.user.id
		) {
			canStore = true;
		}

		return canStore;
	}

	/**
	 * Get the default state to update while moving between apps
	 *
	 * @param module
	 */
	getModuleBaseState(module: string): any {
		let moduleState = null;

		if (module in BASE_STATE) {
			moduleState = BASE_STATE[module];
		}

		return moduleState;
	}

	/**
	 * Return the List of Signal Exception Events that this app needs to manage
	 */
	getSignalExceptionEvents(): any[] {
		return SIGNAL_EXCEPTIONS_EVENTS;
	}

	/**
 	 * Return the state for SMSSettings
 	 */
 	isSMSEnabled(store: any): boolean {
 		return false;
 	}

	/**
	 * Get the Main API Secret Config
	 */
	public getSecretConfigAPI(): string {
		return CONFIG_API;
	}

	/**
	 * Get the Main API Secret Config Route
	 */
	public getSecretConfigRoute(): string {
		return CONFIG_ROUTE;
	}
}
